import moment from 'moment';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { zonedTimeToUtc, format } from 'date-fns-tz';
import useForm from '../../../lib/hooks/useForm';
import { useNotification } from '../../../shared/Notification/useNotification';
import {
  updateRaffle,
  endEarly,
  cancel,
  startNew,
  saveDraft,
  start,
} from '../data';
import prizesSchema from '../prize.schema';

const usePrizeForm = ({ prizeValues }) => {
  const { showSuccess } = useNotification();
  const { t } = useTranslation(['prizes', 'shared']);
  const queryClient = useQueryClient();
  const { mutate } = useMutation(updateRaffle, {
    onSuccess: () => {
      showSuccess({
        content: t('prizes:live.editSuccess'),
        title: t('prizes:live.editTitle'),
      });
      queryClient.invalidateQueries(['getRaffle']);
    },
  });

  const { mutate: mutateSaveDraft } = useMutation(saveDraft, {
    onSuccess: () => {
      showSuccess({
        content: t('prizes:setUp.saveDraftSuccess'),
        title: t('prizes:setUp.title'),
      });
      queryClient.invalidateQueries(['getRaffle']);
    },
  });

  const { mutate: mutateStart } = useMutation(start, {
    onSuccess: () => {
      showSuccess({
        content: t('prizes:setUp.startSuccess'),
        title: t('prizes:setUp.title'),
      });
      queryClient.invalidateQueries(['getRaffle']);
    },
  });

  const { mutate: mutateEndEarly } = useMutation(endEarly, {
    onSuccess: () => {
      showSuccess({
        content: t('prizes:live.editSuccess'),
        title: t('prizes:live.editTitle'),
      });
      queryClient.invalidateQueries(['getRaffle']);
    },
  });

  const { mutate: mutateCancel } = useMutation(cancel, {
    onSuccess: () => {
      showSuccess({
        content: t('prizes:live.cancelSuccess'),
        title: t('prizes:live.cancelRaffleHeader'),
      });
      queryClient.invalidateQueries(['getRaffle']);
    },
  });

  const { mutate: mutateStartNew } = useMutation(startNew, {
    onSuccess: () => {
      showSuccess({ content: t('prizes:pickWinners.startNewSuccess') });
      queryClient.invalidateQueries(['getRaffle']);
    },
  });

  const createInitialDateInputValue = ({ startsAt, endsAt }) => {
    const beginDate = moment(startsAt).format('MM/DD/YYYY');
    const endDate = moment(endsAt).format('MM/DD/YYYY');
    return `${beginDate} - ${endDate}`;
  };

  const dateInputValue =
    prizeValues.startsAt && prizeValues.endsAt
      ? createInitialDateInputValue(prizeValues)
      : '';

  const initialValues = {
    otherInfo: prizeValues.otherInfo || '',
    prizes: prizeValues.prizes || [''],
    startsAt: prizeValues.startsAt || '',
    endsAt: prizeValues.endsAt || '',
    remainingInfo: prizeValues.remainingInfo || {},
    dateInputValue,
    boardTimezone: prizeValues.boardTimezone || '',
  };

  const onSubmit = (values, { resetForm }) => {
    mutate(values);
    resetForm();
  };

  const onEndEarlySubmit = () => {
    mutateEndEarly();
  };

  const onCancel = () => {
    mutateCancel();
  };

  const onStartNew = () => {
    mutateStartNew();
  };

  const form = useForm({
    initialValues,
    onSubmit,
    enableReinitialize: true,
    validationSchema: prizesSchema,
  });

  const onSaveDraft = values => {
    mutateSaveDraft(values);
  };

  const onStart = values => {
    mutateStart(values);
  };

  const handleChangePrize = (event, index) => {
    const { prizes } = form.values;
    const newPrizes = [...prizes];
    newPrizes[index] = event.target.value;
    form.setFieldValue('prizes', newPrizes);
  };

  const addPrize = () => {
    const { prizes } = form.values;
    form.setFieldValue('prizes', [...prizes, '']);
  };

  const removePrize = index => {
    const { prizes } = form.values;
    const newPrizes = [...prizes];
    newPrizes.splice(index, 1);
    form.setFieldValue('prizes', newPrizes);
  };

  const formatPickWinnersStart = (formatEnd, boardTimezone) => {
    return format(formatEnd, "EEEE, MMMM dd 'at' hha zzz", {
      timeZone: boardTimezone,
    });
  };

  const formatDate = (date, time) => {
    return new Date(`${format(date, 'yyyy-MM-dd')} ${time}`);
  };

  const calculateRemainingInfo = (startAt, endAt) => {
    const { boardTimezone } = form.values;
    const today = new Date();

    const formatStart = formatDate(startAt, '00:00:00');
    const timezonedStartAt = zonedTimeToUtc(formatStart, boardTimezone);

    let formatEnd = formatDate(endAt, '23:59:59');
    formatEnd = new Date(formatEnd.getTime() + 1000);
    const timezonedEndAt = zonedTimeToUtc(formatEnd, boardTimezone);

    const initialDate = today > timezonedStartAt ? today : timezonedStartAt;
    const diffDates = timezonedEndAt.valueOf() - initialDate.valueOf();
    const diffInHours = Math.trunc(diffDates / 1000 / 60 / 60);
    const remainingDays = Math.trunc(diffInHours / 24);

    const newInfo = {
      pick_winners_starting: formatPickWinnersStart(formatEnd, boardTimezone),
      weeks: Math.trunc(remainingDays / 7),
      days: remainingDays % 7,
      hours: diffInHours % 24,
    };
    form.setFieldValue('remainingInfo', newInfo);
  };

  const onDateChange = (dates, inputValue) => {
    form.setFieldValue('startsAt', dates[0]);
    form.setFieldValue('endsAt', dates[1]);
    form.setFieldValue('dateInputValue', inputValue);

    calculateRemainingInfo(dates[0], dates[1]);
  };

  return {
    ...form,
    handleChangePrize,
    addPrize,
    removePrize,
    onDateChange,
    onEndEarlySubmit,
    onCancel,
    onStartNew,
    onSaveDraft,
    onStart,
  };
};

export default usePrizeForm;
